/**
 * Employee List Collapse Handler
 * 
 * Handles mobile-only collapse for employee list rows.
 * On desktop, the collapse is always open and links remain clickable.
 * Dynamically updates aria-expanded when resizing between desktop and mobile.
 */
document.addEventListener('DOMContentLoaded', () => {
	// Bootstrap Collapse must exist
	if (typeof window.bootstrap?.Collapse !== 'function') {
		return; // Exit early if Bootstrap's Collapse is not available
	}

	const tabs = document.querySelectorAll('.staff-list__tab'); // All tab headers in the staff list

	/**
	 * Check if the current viewport is mobile
	 * @returns {boolean}
	 */
	function isMobile() {
		return window.matchMedia('(max-width: 767px)').matches;
	}

	/**
	 * Cache original employee name link data (run once)
	 * Stores href + data attributes on the tab element
	 */
	function cacheNameLinkData(tab) {
		if (tab.dataset.nameLinkCached === 'true') {
			return;
		}

		const link = tab.querySelector('.staff-list__name a');
		const nameSpan = link?.querySelector('span');

		if (!link || !nameSpan) {
			return;
		}

		tab.dataset.nameLinkCached = 'true';
		tab.dataset.nameLinkHref = link.getAttribute('href');
		tab.dataset.nameLinkPresentation =
			link.getAttribute('data-category-content-presentation') || '';
		tab.dataset.nameLinkName =
			link.getAttribute('data-category-content-name') || nameSpan.textContent.trim();
	}

	/**
   * Remove the link around the employee name (mobile)
   * Also prevent double analytics by moving data-* attributes to the tab
   */
	function removeNameLink(tab) {
		const link = tab.querySelector('.staff-list__name a');
		const nameSpan = link?.querySelector('span');

		if (!link || !nameSpan) {
			return;
		};

		// Move data attributes to the tab
		const dataAttrs = ['data-category-content-presentation', 'data-category-content-name'];
		dataAttrs.forEach((attr) => {
			const value = link.getAttribute(attr);
			if (value) {
				tab.setAttribute(attr, value);
				link.removeAttribute(attr); // Remove from original link to prevent double firing
			}
		});

		// Replace link with span
		link.replaceWith(nameSpan);

		// Mark that the tab now has mobile data attributes
		tab.dataset.mobileMode = 'true';
	}

	/**
	 * Restore the link around the employee name (desktop)
	 * Restores data-* attributes to the link, removes them from the tab
	 */
	function restoreNameLink(tab) {
		const container = tab.querySelector('.staff-list__name');
		const nameSpan = container?.querySelector('span');

		if (!container || !nameSpan || nameSpan.closest('a')) return;

		const href = tab.dataset.nameLinkHref;
		if (!href) {
			return;
		};

		const link = document.createElement('a');
		link.setAttribute('href', href);
		link.setAttribute('itemprop', 'url');

		// Restore analytics attributes to the link
		if (tab.dataset.nameLinkPresentation) {
			link.setAttribute(
				'data-category-content-presentation',
				tab.dataset.nameLinkPresentation
			);
		}

		if (tab.dataset.nameLinkName) {
			link.setAttribute(
				'data-category-content-name',
				tab.dataset.nameLinkName
			);
		}

		link.appendChild(nameSpan);
		container.appendChild(link);

		// Remove analytics data from tab to prevent double firing
		tab.removeAttribute('data-category-content-presentation');
		tab.removeAttribute('data-category-content-name');
		tab.dataset.mobileMode = 'false';
	}


	/**
	 * Setup collapse behavior for a given tab
	 * @param {HTMLElement} tab 
	 */
	function setupTab(tab) {
		const targetId = tab.getAttribute('data-target'); // The ID of the collapse content
		const target = document.querySelector(targetId);

		if (!target) {
			return; // Skip if the collapse target doesn't exist
		}

		// Cache link data once
		cacheNameLinkData(tab);

		// Initialize Bootstrap collapse but do NOT toggle automatically
		const bsCollapse = new bootstrap.Collapse(target, { toggle: false });

		// Update aria-expanded when the collapse shows on mobile
		target.addEventListener('show.bs.collapse', () => {
			if (isMobile()) {
				tab.setAttribute('aria-expanded', 'true'); // Mark tab as expanded for screen readers
			}
		});

		// Update aria-expanded when the collapse hides on mobile
		target.addEventListener('hide.bs.collapse', () => {
			if (isMobile()) {
				tab.setAttribute('aria-expanded', 'false'); // Mark tab as collapsed for screen readers
			}
		});

		// Click handler for mobile toggle
		tab.addEventListener('click', (event) => {
			if (!isMobile()) {
				return; // Do not toggle collapse on desktop
			}

			event.preventDefault(); // Prevent default behavior
			bsCollapse.toggle(); // Toggle the collapse manually
		});

		// Keyboard support for mobile (Enter / Space)
		tab.addEventListener('keydown', (event) => {
			if (!isMobile()) {
				return; // Ignore keyboard interaction on desktop
			}

			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault(); // Prevent page scroll on Space
				bsCollapse.toggle();
			}
		});
	}

	tabs.forEach(setupTab); // Apply collapse behavior to each tab

	/**
	 * Reset all collapses based on viewport
	 * - Open on desktop
	 * - Closed on mobile
	 * Also updates aria-expanded dynamically
	 */
	function resetCollapse() {
		tabs.forEach((tab) => {
			const targetId = tab.getAttribute('data-target');
			const target = document.querySelector(targetId);

			if (!target) {
				return; // Skip if target doesn't exist
			}

			if (isMobile()) {
				// On mobile, collapse all sections by default
				target.classList.remove('show'); // Hide content

				// Remove link and move tracking data to the tab
				removeNameLink(tab);

				tab.setAttribute('role', 'button');
				tab.setAttribute('tabindex', '0');
				tab.setAttribute('aria-controls', targetId.replace('#', ''));
				tab.setAttribute('aria-expanded', 'false');

				if (tab.dataset.nameLinkPresentation) {
					tab.setAttribute(
						'data-category-content-presentation',
						tab.dataset.nameLinkPresentation
					);
				}

				if (tab.dataset.nameLinkName) {
					tab.setAttribute(
						'data-category-content-name',
						tab.dataset.nameLinkName
					);
				}
			} else {
				// On desktop, keep all sections open
				target.classList.add('show'); // Show content

				// Restore link and clean up mobile-only attributes
				restoreNameLink(tab);

				tab.removeAttribute('role');
				tab.removeAttribute('tabindex');
				tab.removeAttribute('aria-expanded');
				tab.removeAttribute('aria-controls');
				tab.removeAttribute('data-category-content-presentation');
				tab.removeAttribute('data-category-content-name');
			}
		});
	}

	// Attach resize handler using global debounce if available
	if (typeof debounce === 'function') {
		window.addEventListener('resize', debounce(resetCollapse, 100));
	} else {
		window.addEventListener('resize', resetCollapse);
	}

	// Initialize collapse state on page load
	resetCollapse();

	/**
	 * Scroll the relevant list section into view whenever a pagination
	 * button inside it is clicked. Supports multiple independent lists,
	 * each with their own '.list-section' wrapper and pagination controls.
	 *
	 * Uses event delegation on `document` (capture phase) so it also works
	 * with pagination controls that get re-rendered/replaced after an AJAX
	 * page change. Capture phase is required because the pagination plugin's
	 * own bubble-phase click handler swaps the DOM (via AJAX) before bubbling
	 * reaches `document` — by then the original clicked link is detached and
	 * `closest('.list-section')` would return null. Running in capture phase
	 * ensures this handler sees the link while it's still attached.
	 *
	 * @param {MouseEvent} event
	 */
	document.addEventListener('click', (event) => {
		const paginationButton = event.target.closest('.pagination .page-link');

		if (!paginationButton) {
			return;
		}

		// Find the specific list section that owns this pagination control,
		// so each list (if there are multiple on the page) scrolls independently
		const listSection = paginationButton.closest('.list-section');

		if (!listSection) {
			return;
		}

		listSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, true); // capture: true — see JSDoc above
});
